// src\themes\lino\elements\Product\ProductInformation.tsx
'use client';

import { Minus, Plus } from 'lucide-react';

import { useState } from 'react';

import Link from 'next/link';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useDictionary } from '@/core/hooks/use-dictionary';
import {
  MainShopProduct,
  ProductColor,
} from '@/core/models/shop-product-model';

import { cn } from '@/core/lib/utils';
import { formatPrice, formatWeight } from '@/utils/formatUtils';
import { AddToCartButton } from '@/themes/lino/elements/Product/AddToCartButton';

type ProductInformationProps = {
  product: MainShopProduct;
  colors: ProductColor[];
};

export default function ProductInformation({
  product,
  colors,
}: ProductInformationProps) {
  const [openState, setOpenState] = useState({ details: false, sizes: false });
  const { dictionary } = useDictionary();

  const filledMetaFields = product.meta_fields.filter(
    item =>
      item.value !== null &&
      item.value !== undefined &&
      String(item.value).trim() !== ''
  );

  return (
    <div className="lg:col-span-1 flex flex-col text-primary-900 space-y-8">
      <div className="space-y-3">
        {/* heading */}
        <div className="space-y-2.5">
          <h1 className="text-2xl font-light leading-snug tracking-tight">
            {product.title + ' ' + product.color_title}
          </h1>
          {product.description && (
            <p className="text-[13px] text-primary-600 leading-6">{product.description}</p>
          )}
        </div>

        {/* price and discount */}
        <div className="flex gap-3 items-center py-3 border-t border-primary-100 mt-2">
          {product.discount_percent > 0 ? (
            <div className='flex items-baseline gap-3 pt-3'>
              <span className="text-primary-400 line-through text-sm">
                {formatPrice(Number(product.price))} {dictionary.ui?.product.currency}
              </span>
              <span className='text-xl font-medium text-primary-900'>
                {formatPrice(Number(product.final_price))} {dictionary.ui?.product.currency}
              </span>
            </div>
          ) : (
            <span className='text-xl font-medium text-primary-900 pt-3'>
              {formatPrice(Number(product.price))} {dictionary.ui?.product.currency}
            </span>
          )}
        </div>

        {/* color */}
        <div className="flex flex-wrap gap-3 py-2">
          {Array.isArray(colors) ? colors?.map(color => (
            <Tooltip key={color.product_id}>
              <TooltipTrigger asChild>
                <Link href={`/shop/product/${color.product_id}/${product.slug}`}>
                  <div
                    className={cn("size-6 rounded-full border border-primary-200 cursor-pointer hover:ring-1 hover:ring-offset-2 hover:ring-primary-900 transition-all",
                      //@ts-ignore
                      product.id === color.product_id && "ring-1 ring-offset-2 ring-primary-900"
                    )}
                    style={{ backgroundColor: color.color_code }}
                    title={color.color_title}
                  />
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-sm text-primary-0">{color.color_title}</p>
              </TooltipContent>
            </Tooltip>
          )) : <Tooltip >
            <TooltipTrigger asChild>
              <div
                className={cn("size-6 rounded-full border border-primary-200 cursor-pointer hover:ring-2 hover:ring-primary-300 transition-transform hover:scale-110",
                )}
                style={{ backgroundColor: product.color_code }}
                title={product.color_title}
              />
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-sm text-primary-0">{product.color_title}</p>
            </TooltipContent>
          </Tooltip>}
        </div>

        {/* detail */}
        <div className="border-t border-primary-100">
          {filledMetaFields.length > 0 && (
            <Collapsible
              open={openState.details}
              onOpenChange={open =>
                setOpenState(prev => ({ ...prev, details: open }))
              }
            >
              <CollapsibleTrigger asChild>
                <button className="flex items-center gap-2 text-[11px] uppercase tracking-[0.12em] py-4 text-primary-900 w-full border-b border-primary-100">
                  {openState.details ? (
                    <Minus className="size-3.5 stroke-[1.5]" />
                  ) : (
                    <Plus className="size-3.5 stroke-[1.5]" />
                  )}
                  {dictionary.ui?.product.details.show}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                {filledMetaFields.map((item, index) => (
                  <p key={index} className="text-[13px] text-primary-600 my-2.5">
                    {item.label} : {item.value}
                  </p>
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}
          <Collapsible
            open={openState.sizes}
            onOpenChange={open =>
              setOpenState(prev => ({ ...prev, sizes: open }))
            }
          >
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-2 text-[11px] uppercase tracking-[0.12em] text-primary-900 py-4 w-full border-b border-primary-100">
                {openState.sizes ? (
                  <Minus className="size-3.5 stroke-[1.5]" />
                ) : (
                  <Plus className="size-3.5 stroke-[1.5]" />
                )}
                {dictionary.ui?.product.sizes.show}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              {product.fabric_area && (
                <p className="text-[13px] text-primary-600 my-2.5">
                  {dictionary.forms?.fabric_area} :{' '}
                  <span>{product.fabric_area}</span>
                </p>
              )}

              {product.weight && (
                <p className="text-[13px] text-primary-600 my-2.5">
                  {formatWeight(product.weight, dictionary)}
                </p>
              )}
              {product.overalsize && (
                <p className="text-[13px] text-primary-600 my-2.5">
                  {dictionary.forms?.overalsize} :{' '}
                  <span>{product.overalsize}</span>
                </p>
              )}
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>

      {/* button */}
      <div>
        <AddToCartButton
          productInfo={product}
          productInventory={product.inventory}
        />
        {product.content && (
          <p className="text-[13px] text-primary-600 mt-5 leading-6">{product.content}</p>
        )}
      </div>
    </div>
  );
}